import clsx from "clsx";
import { useRef, useState } from "react";
import { useOutsideClickHandler } from "@/hooks";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/16/solid";
import GradientBorderRadius, { RadiusStyles } from "./gradient-border-radius";

export type DataProp = {
  value: string | null;
  label: string;
  component?: React.ReactNode;
};

const Select = ({
  data = [],
  initValue = { label: "Placeholder", value: null },
  onChange = () => {},
  radius = "normal",
  className = "",
}: {
  data: DataProp[];
  initValue?: DataProp;
  onChange?: (value: DataProp) => void;
  radius?: keyof typeof RadiusStyles;
  className?: string;
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [selected, setSelected] = useState<DataProp>({
    label: initValue.label,
    value: initValue.value,
    component: initValue.component,
  });

  const inputRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useOutsideClickHandler(() => setIsVisible(false), [inputRef, dropdownRef]);

  return (
    <div className={clsx("flex flex-col h-full")}>
      <GradientBorderRadius
        hover="r-100"
        radius={radius}
        gradient="default-border"
        onClick={() => {
          setIsVisible((prev) => !prev);
        }}
        className="flex-1 h-full"
        ref={inputRef}
      >
        <div
          className={clsx(
            "min-w-[6.25rem]",
            "text-xs",
            "cursor-pointer select-none",
            "flex items-center justify-between pl-[0.3125rem] pr-[0.05rem] py-[0.15rem] bg-transparent bg-gradient-to-r font-semibold",
            className
          )}
        >
          {selected.component || selected.label}
          <div className="flex items-center justify-center w-[1.0000rem] h-full">
            {isVisible ? (
              <ChevronUpIcon color="white" />
            ) : (
              <ChevronDownIcon color="white" />
            )}
          </div>
        </div>
      </GradientBorderRadius>
      <div className="relative" style={{ zIndex: 1000 }}>
        <div
          className={clsx(isVisible ? "absolute inset-0 z-10" : "hidden")}
          ref={dropdownRef}
        >
          <div className="mt-[0.4375rem]">
            <GradientBorderRadius gradient="100" overflow>
              <div
                className={clsx(
                  "flex gap-[0.3125rem] flex-col",
                  "text-xs",
                  "bg-black"
                )}
              >
                {data.map(({ label, value, component }, index) => (
                  <div
                    className={`relative gradient-box cursor-pointer hover:bg-table-hover p-[0.3125rem] select-none before:hover:bg-gradient-100 ${
                      index === 0
                        ? "before:pb-[0.0625rem]"
                        : index === data.length - 1
                          ? "before:pt-[0.0625rem]"
                          : index === 1 || index === 2
                            ? "before:py-[0.0625rem]"
                            : ""
                    }`}
                    onClick={() => {
                      setIsVisible(false);
                      setSelected({ label, value, component });
                      onChange({ label, value, component });
                    }}
                    key={`select-${value}`}
                  >
                    {component || label}
                  </div>
                ))}
              </div>
            </GradientBorderRadius>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Select;
